import { SUPPORTED_EXTENSIONS, SUPPORTED_MIME_TYPES } from './constants';

export type SniffedType = 'jpeg' | 'png' | 'webp' | 'bmp' | 'pdf' | null;

export function extensionOf(name: string): string {
  const idx = name.lastIndexOf('.');
  if (idx < 0) return '';
  return name.slice(idx + 1).toLowerCase();
}

export function isSupportedExtension(name: string): boolean {
  return (SUPPORTED_EXTENSIONS as readonly string[]).includes(extensionOf(name));
}

export function isSupportedMime(mime: string): boolean {
  return (SUPPORTED_MIME_TYPES as readonly string[]).includes((mime || '').toLowerCase());
}

function matches(bytes: Uint8Array, offset: number, signature: number[]): boolean {
  if (bytes.length < offset + signature.length) return false;
  for (let i = 0; i < signature.length; i += 1) {
    if (bytes[offset + i] !== signature[i]) return false;
  }
  return true;
}

const ASCII = (s: string) => Array.from(s, (c) => c.charCodeAt(0));

/**
 * Authoritative magic-byte sniffing. The filename is never trusted.
 */
export function sniffFileType(bytes: Uint8Array): SniffedType {
  if (!bytes || bytes.length < 4) return null;
  // JPEG: FF D8 FF
  if (matches(bytes, 0, [0xff, 0xd8, 0xff])) return 'jpeg';
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (matches(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  // WEBP: "RIFF" .... "WEBP"
  if (matches(bytes, 0, ASCII('RIFF')) && matches(bytes, 8, ASCII('WEBP'))) return 'webp';
  // BMP: "BM"
  if (matches(bytes, 0, ASCII('BM'))) return 'bmp';
  // PDF: "%PDF-" (allow a small leading offset, some writers prepend junk)
  const head = bytes.subarray(0, Math.min(bytes.length, 1024));
  const pdfSig = ASCII('%PDF-');
  for (let i = 0; i <= head.length - pdfSig.length && i < 8; i += 1) {
    if (matches(head, i, pdfSig)) return 'pdf';
  }
  return null;
}

export function sniffedTypeIsPdf(t: SniffedType): boolean {
  return t === 'pdf';
}

export function readHeaderBytes(file: Blob, length = 64): Promise<Uint8Array> {
  const slice = file.slice(0, length);
  if (typeof slice.arrayBuffer === 'function') {
    return slice.arrayBuffer().then((buf) => new Uint8Array(buf));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsArrayBuffer(slice);
  });
}
